import { useCallback } from 'react'

import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SharePointSite,
  sharepointSitesQueryKeys,
} from '@/hooks/sharepoint/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

export const DEFAULT_SITE_LIMIT = 50
const MAX_FETCH_ALL_SITE_PAGES = 200

export interface FetchSharepointSitesResponse {
  data: SharePointSite[]
  has_more: boolean
  count: number
  next_cursor?: string | null
  access_limited?: boolean
  selected_sites_mode?: boolean
}

interface UseSharepointSitesArgs<T> {
  enabled?: boolean
  fetchAll?: boolean
  limit?: number
  orgId: UUID | ''
  rootsOnly?: boolean
  select?: (data: FetchSharepointSitesResponse) => T
}

interface FetchSharepointSitesOptions {
  cursor?: string | null
  limit?: number
  rootsOnly?: boolean
}

interface UseInfiniteSharepointSitesArgs<
  T = InfiniteData<FetchSharepointSitesResponse>,
> {
  enabled?: boolean
  limit?: number
  orgId: UUID | ''
  rootsOnly?: boolean
  select?: (data: InfiniteData<FetchSharepointSitesResponse>) => T
}

export const fetchSharepointSites = async (
  orgId: UUID | '',
  accessToken: string,
  options: FetchSharepointSitesOptions = {},
): Promise<FetchSharepointSitesResponse> => {
  if (!orgId) throw new Error('No org ID provided.')
  const params = new URLSearchParams()
  if (options.limit) params.set('limit', String(options.limit))
  if (options.cursor) params.set('cursor', options.cursor)
  if (options.rootsOnly) params.set('roots_only', 'true')

  const query = params.toString()
  const url = `${API_URL}/api/v1/organizations/${orgId}/sharepoint/sites${query ? `?${query}` : ''}`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as FetchSharepointSitesResponse
}

export const fetchAllSharepointSites = async (
  orgId: UUID | '',
  accessToken: string,
  options: Omit<FetchSharepointSitesOptions, 'cursor'> = {},
): Promise<FetchSharepointSitesResponse> => {
  const sites: SharePointSite[] = []
  let cursor: string | null = null
  let latestResponse: FetchSharepointSitesResponse | null = null
  let pageCount = 0
  const seenCursors = new Set<string>()

  do {
    if (pageCount >= MAX_FETCH_ALL_SITE_PAGES) {
      console.warn(
        `fetchAllSharepointSites: exceeded ${MAX_FETCH_ALL_SITE_PAGES} pages; stopping early.`,
      )
      break
    }

    if (cursor && seenCursors.has(cursor)) {
      console.warn(
        `fetchAllSharepointSites: encountered repeated cursor "${cursor}"; stopping early.`,
      )
      break
    }

    if (cursor) {
      seenCursors.add(cursor)
    }

    latestResponse = await fetchSharepointSites(orgId, accessToken, {
      cursor,
      limit: options.limit,
      rootsOnly: options.rootsOnly,
    })
    pageCount += 1
    sites.push(...latestResponse.data)
    cursor =
      latestResponse.has_more && latestResponse.next_cursor
        ? latestResponse.next_cursor
        : null
  } while (cursor)

  if (latestResponse == null) {
    return {
      data: [],
      has_more: false,
      count: 0,
      next_cursor: null,
    }
  }

  return {
    ...latestResponse,
    data: sites,
    count: sites.length,
    has_more: cursor != null,
    next_cursor: cursor,
  }
}

export const useSharepointSites = <T = SharePointSite[]>({
  enabled = true,
  fetchAll = false,
  limit,
  orgId,
  rootsOnly = false,
  select,
}: UseSharepointSitesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: FetchSharepointSitesResponse): T => {
      return select ? select(data) : (data.data as unknown as T)
    },
    [select],
  )

  return useQuery({
    queryKey: sharepointSitesQueryKeys.organization({
      orgId,
      rootsOnly,
      fetchAll,
    }),
    queryFn: () =>
      fetchAll
        ? fetchAllSharepointSites(orgId, accessToken, { limit, rootsOnly })
        : fetchSharepointSites(orgId, accessToken, { limit, rootsOnly }),
    enabled: !!orgId && enabled,
    staleTime: Infinity,
    select: selectFn,
  })
}

export const useInfiniteSharepointSites = <T = SharePointSite[]>({
  enabled = true,
  limit = DEFAULT_SITE_LIMIT,
  orgId,
  rootsOnly = false,
  select,
}: UseInfiniteSharepointSitesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: InfiniteData<FetchSharepointSitesResponse>): T => {
      if (select) {
        return select(data)
      }
      return data.pages.flatMap((page) => page.data) as unknown as T
    },
    [select],
  )

  return useInfiniteQuery({
    queryKey: sharepointSitesQueryKeys.paginated({
      orgId,
      rootsOnly,
      limit,
    }),
    queryFn: ({ pageParam }) =>
      fetchSharepointSites(orgId, accessToken, {
        cursor: pageParam ?? null,
        limit,
        rootsOnly,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: !!orgId && enabled,
    staleTime: 60 * 1000,
    select: selectFn,
  })
}
